/**
 * DodgeRoll — pure, Phaser-free dodge-roll frame-window logic.
 *
 * DODGE input → brief movement burst + i-frame window + recovery + cooldown.
 * Caller is responsible for checking isHitstunned / isBeingThrown / isAirborne
 * before calling beginDodgeRoll().
 *
 * Controls (P1: SHIFT · P2: NUMPAD_7 · Gamepad: L2/button 6)
 */

/** Frames at the START of the roll that grant full invulnerability (i-frames). */
export const DODGE_IFRAME_FRAMES = 11;

/** Total active-roll duration in frames (i-frames + recovery). */
export const DODGE_TOTAL_FRAMES = 26;

/** Frames of cooldown after the roll ends before another dodge is allowed. */
export const DODGE_COOLDOWN_FRAMES = 45;

/** Pixels per frame applied in the roll direction during the active window. */
export const DODGE_SPEED = 4.5;

export interface DodgeRollState {
  /** True while the roll animation is playing (includes recovery frames after i-frames). */
  active: boolean;
  /** Frames remaining in the active roll (counts down from DODGE_TOTAL_FRAMES to 0). */
  framesLeft: number;
  /** Cooldown frames remaining after the roll ends; 0 = ready to dodge again. */
  cooldownLeft: number;
  /** Normalized x direction at roll start (-1, 0, or 1). */
  dirX: number;
  /** Normalized y direction at roll start (-1, 0, or 1). */
  dirY: number;
}

export function createDodgeRollState(): DodgeRollState {
  return { active: false, framesLeft: 0, cooldownLeft: 0, dirX: 0, dirY: 0 };
}

/** True when a dodge can be triggered (not active, cooldown expired). */
export function canDodge(state: DodgeRollState): boolean {
  return !state.active && state.cooldownLeft === 0;
}

/**
 * Begin a dodge roll in the given direction.
 * dirX / dirY should each be -1, 0, or 1. If both are 0 pass the entity's
 * facing direction as dirX so the roll always has a travel component.
 */
export function beginDodgeRoll(state: DodgeRollState, dirX: number, dirY: number): void {
  state.active = true;
  state.framesLeft = DODGE_TOTAL_FRAMES;
  state.cooldownLeft = 0;
  state.dirX = dirX;
  state.dirY = dirY;
}

/**
 * Advance the dodge-roll state one frame.
 * Active roll counts down; on expiry, transitions into the cooldown window.
 */
export function tickDodgeRoll(state: DodgeRollState): void {
  if (state.active) {
    state.framesLeft -= 1;
    if (state.framesLeft <= 0) {
      state.active = false;
      state.framesLeft = 0;
      state.cooldownLeft = DODGE_COOLDOWN_FRAMES;
    }
  } else if (state.cooldownLeft > 0) {
    state.cooldownLeft -= 1;
  }
}

/**
 * True during the invulnerability window at the start of the roll.
 * I-frames cover the first DODGE_IFRAME_FRAMES frames of the active roll;
 * remaining frames are recovery (visible but vulnerable).
 */
export function isDodgeInvulnerable(state: DodgeRollState): boolean {
  return state.active && state.framesLeft > DODGE_TOTAL_FRAMES - DODGE_IFRAME_FRAMES;
}
